import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import {
  mockListCompras,
  mockCreateStockEntry,
  mockEmpresas,
} from '../data/comprasMock';

/**
 * useCompras()
 *
 * Listado de compras (stock entries) + alta de una compra nueva +
 * empresas (internas y colaboradoras) para el selector.
 *
 * Backend III.9 aplicado:
 *
 *   GET  /api/inventario/stock-entries  → { data:[...], count }
 *   POST /api/inventario/stock-entries  → { stock_entry, activos:{ids,references} }
 *
 * Las empresas se piden a /options (mismo endpoint que el resto del
 * módulo). Se precargan ambas listas (interno + colab) en `load()`
 * para que `empresasDe(scope)` siga siendo síncrono y la página no
 * tenga que cambiar.
 */

const USE_MOCK = false;
const wait = (ms) => new Promise((r) => setTimeout(r, ms));

export function useCompras() {
  const [compras, setCompras] = useState([]);
  const [empresasInt, setEmpresasInt] = useState([]);
  const [empresasCol, setEmpresasCol] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (USE_MOCK) {
        await wait(200);
        setCompras(mockListCompras());
      } else {
        // Compras + las dos listas de empresas en paralelo. Los
        // catch individuales protegen al listado entero de un
        // fallo aislado en /options.
        const [comprasRes, empIntRes, empColRes] = await Promise.all([
          api.get('/stock-entries'),
          api.get('/options', { link: 'company', scope: 'interno' }).catch(() => null),
          api.get('/options', { link: 'company', scope: 'colab' }).catch(() => null),
        ]);
        setCompras(comprasRes?.data || []);
        setEmpresasInt(empIntRes?.data || []);
        setEmpresasCol(empColRes?.data || []);
      }
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const empresasDe = useCallback(
    (scope) => {
      if (USE_MOCK) return mockEmpresas(scope);
      return scope === 'colab' ? empresasCol : empresasInt;
    },
    [empresasInt, empresasCol]
  );

  /**
   * Crea una compra. `tipo` se mantiene como parámetro por compatibilidad
   * con la firma del mock (la página lo pasa). En modo real no se usa —
   * el backend conoce el tipo a partir de asset_type_id en el payload
   * y devuelve { stock_entry, activos:{ids,references} } enriquecido.
   */
  const crearCompra = useCallback(
    async (payload, tipo) => {
      setCreating(true);
      try {
        let result;
        if (USE_MOCK) {
          await wait(280);
          result = mockCreateStockEntry({ payload, tipo });
        } else {
          result = await api.post('/stock-entries', payload);
        }
        await load();
        return result;
      } finally {
        setCreating(false);
      }
    },
    [load]
  );

  /**
   * Edita una compra existente. `payload` solo contiene los campos
   * editables (proveedor, fecha_compra, coste, factura, notas, empresa).
   * El backend propaga a los N eventos `compra` vinculados.
   * No editable: cantidad, asset_type_id, scope, common_fields,
   * per_unit_fields — eso requeriría tocar los activos creados.
   */
  const editarCompra = useCallback(
    async (id, payload) => {
      const res = await api.put(`/stock-entries/${id}`, payload);
      await load();
      return res;
    },
    [load]
  );

  /**
   * Soft-delete de una compra. `deleteReason` es obligatorio. El
   * backend marca la cabecera como deleted + los N eventos compra
   * asociados. NO toca los activos (siguen vivos en la tabla
   * dinámica) — el modal del frontend avisa de esto.
   */
  const eliminarCompra = useCallback(
    async (id, deleteReason) => {
      const res = await api.del(`/stock-entries/${id}`, {
        delete_reason: deleteReason,
      });
      await load();
      return res;
    },
    [load]
  );

  return {
    compras,
    loading,
    error,
    creating,
    refresh: load,
    empresasDe,
    crearCompra,
    editarCompra,
    eliminarCompra,
    isMock: USE_MOCK,
  };
}
